import React, { useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { registerObject } from "go-form";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { CustomValidationConfig, CustomValidationConstraint, CustomValidationError } from "go-form/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	ContactApi,
	ContactApiSearchSelect,
	InvoiceApi,
} from "../../../../../../../../../../services/Api/Organization/types";
import CreateContactModalForm from "../../../../components/CreateContactModalForm";

interface Props {
	form: UseFormReturn<InvoiceApi>;
	setErrors: React.Dispatch<React.SetStateAction<CustomValidationError<InvoiceApi>[]>>;
}

const InvoiceFormClients = ({ form, setErrors }: Props) => {
	const {
		control,
		watch,
		formState: { errors },
		register,
		setValue,
	} = form;
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const [newClientModal, setNewClientModal] = useState<"recipient" | "receiver" | undefined>();
	const [newContact, setNewContact] = useState<ContactApi>();

	const searchContacts = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getContactSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const handleCreateClient = (type: "recipient" | "receiver", value?: string) => {
		setNewContact({ name: value } as ContactApi);
		setNewClientModal(type);
	};

	const handleSaveContact = (newClient: ContactApi, type: "recipient" | "receiver") => {
		if (type === "recipient") {
			setValue("recipient_id", newClient.id);
			setValue("recipient", newClient);
		}
		if (type === "receiver") {
			setValue("receiver_id", newClient.id);
			setValue("receiver", newClient);
		}

		setNewClientModal(undefined);
	};
	const handleChangeClient = (type: "recipient" | "receiver", clientValue?: ContactApiSearchSelect) => {
		if (type === "recipient") {
			registerObject(register, `recipient`, ["name", "id"]);
			setValue(
				"recipient",
				clientValue ? { name: clientValue.label, id: Number(clientValue.id), tax_id_no: "" } : null
			);
		}
		if (type === "receiver") {
			registerObject(register, `receiver`, ["name", "id"]);
			setValue(
				"receiver",
				clientValue ? { name: clientValue.label, id: Number(clientValue.id), tax_id_no: "" } : undefined
			);
		}
	};

	const inputValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	return (
		<>
			<fieldset className="form-group">
				<h5>{t("modules.invoice.field.recipient_receiver.title")}</h5>
				<div className="row">
					<div className="col-lg-10">
						<div className="row">
							<div className="col-md-6">
								<FormSelectGroup
									control={control}
									loadOptions={searchContacts}
									label={t("modules.invoice.field.recipient.title")}
									onChange={(_, recipient) => handleChangeClient("recipient", recipient)}
									name="recipient_id"
									getOptionValue={(option) => option.id}
									defaultValue={{
										label: watch("recipient")?.name,
										id: watch("recipient_id"),
									}}
									errors={errors}
									data-testid="recipient_id"
									onCreateOption={(obj) => handleCreateClient("recipient", obj)}
									path={`/${organization.id}/contacts/`}
									customValidationConfig={inputValidationConfig}
								/>
							</div>
							<div className="col-md-6">
								<FormSelectGroup
									control={control}
									loadOptions={searchContacts}
									label={t("modules.invoice.field.receiver.title")}
									onChange={(_, recipient) => handleChangeClient("receiver", recipient)}
									name="receiver_id"
									getOptionValue={(option) => option.id}
									defaultValue={{
										label: watch("receiver")?.name,
										id: watch("receiver_id"),
									}}
									errors={errors}
									data-testid="receiver_id"
									onCreateOption={(obj) => handleCreateClient("receiver", obj)}
									path={`/${organization.id}/contacts/`}
								/>
							</div>
						</div>
					</div>
				</div>
			</fieldset>
			{newClientModal && (
				<CreateContactModalForm
					isShown={!!newClientModal}
					value={newContact?.name || ""}
					onHide={() => setNewClientModal(undefined)}
					onSave={(data) => handleSaveContact(data, newClientModal)}
				/>
			)}
		</>
	);
};

export default InvoiceFormClients;
